import { useActionRegistry, usePalette } from "@hq/command-palette";
import { Button, Kbd, toast, useShortcut } from "@z/ui";
import { BookOpen, FilePlus2, PanelsTopLeft, Search, type LucideIcon } from "lucide-react";
import { useState } from "react";
import { useWorkspace } from "./WorkspaceContext";

export function WorkspaceEmpty() {
  const palette = usePalette();
  const registry = useActionRegistry();
  const workspace = useWorkspace();
  const [creating, setCreating] = useState(false);

  const newNote = async () => {
    if (creating) return;
    setCreating(true);
    try {
      await registry.run("notes.new", {});
    } catch {
      toast({
        title: "Could not create note",
        description: "Please try again.",
        variant: "danger",
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 items-center justify-center overflow-y-auto px-6 py-10">
      <section aria-labelledby="workspace-empty-title" className="my-auto w-full max-w-sm pb-12">
        <div
          aria-hidden="true"
          className="mx-auto mb-6 flex size-14 items-center justify-center rounded-lg bg-surface text-fg-muted shadow-sm ring-1 ring-border/60"
        >
          <PanelsTopLeft className="size-7" strokeWidth={1.25} />
        </div>
        <h1
          id="workspace-empty-title"
          className="text-balance text-center text-xl font-medium tracking-tight"
        >
          No tabs open
        </h1>
        <p className="mx-auto mt-2 max-w-xs text-pretty text-center text-sm leading-relaxed text-fg-muted">
          Find something in your workspace, or start a new note.
        </p>
        <div className="mt-7 flex flex-col gap-2">
          <EmptyAction
            title="Search workspace"
            icon={Search}
            shortcutId="palette.toggle"
            onClick={palette.open}
            primary
          />
          <EmptyAction
            title={creating ? "Creating note…" : "New note"}
            icon={FilePlus2}
            shortcutId="notes.new"
            onClick={() => void newNote()}
            disabled={creating}
          />
          <EmptyAction
            title="Open Daily Notes"
            icon={BookOpen}
            shortcutId="go.daily"
            onClick={() => workspace.open({ paneType: "daily" })}
          />
        </div>
      </section>
    </div>
  );
}

function EmptyAction({
  title,
  icon: Icon,
  shortcutId,
  onClick,
  primary = false,
  disabled = false,
}: {
  title: string;
  icon: LucideIcon;
  shortcutId: string;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
}) {
  const shortcut = useShortcut(shortcutId);
  return (
    <Button
      variant={primary ? "secondary" : "ghost"}
      onClick={onClick}
      disabled={disabled}
      className="h-12 w-full justify-start gap-3 rounded-lg px-4 font-normal"
    >
      <Icon aria-hidden="true" className="text-fg-muted" />
      <span className="flex-1 text-left">{title}</span>
      {shortcut && (
        <span aria-hidden="true" className="hidden sm:inline-flex">
          <Kbd keys={shortcut} />
        </span>
      )}
    </Button>
  );
}
